export default {
  id: 'analysis-mode',
  title: 'Analysis Mode',
  content: `## Analysis Mode

Analysis Mode is the stock screening tab inside Nyx AI. It ranks all **51 Nifty50 stocks** daily by a composite confidence score — giving you a ranked list of the highest-conviction setups of the day.

---

## Where to Find It

**Profile → Nyx AI Coach → Analysis tab**

---

## The Composite Confidence Score

Each stock receives a score from 0 to 100 built from four weighted signals:

| Signal | What it measures |
|---|---|
| Confidence | AI conviction based on the full set of available data for this stock |
| Technical | RSI, MACD, Bollinger Band, and SMA alignment |
| Volume | Current session volume ratio vs 20-day average |
| Sentiment | News sentiment score from RSS feeds (VADER-scored, last 24h) |

**The weights are not fixed.** They recalibrate every Monday based on which signals predicted correctly the prior week. If volume was the most predictive factor last week, its weight increases this week.

---

## Self-Improving Weights

Every prediction made by Analysis Mode is recorded. Every Wednesday and Friday, actual outcomes (price change over 1 day and 3 days) are matched against predictions from those dates.

On Monday, the weight recalibrator compares:
- Which signal component had the highest correlation with correct predictions
- Which had low or negative correlation

Weights shift accordingly. Over time, this makes the composite score increasingly accurate for NSE market conditions.

---

## What the Ranked List Shows

Each stock in the list displays:

| Field | Description |
|---|---|
| Rank | 1–51, sorted by composite score (highest = most conviction) |
| Symbol | NSE stock ticker |
| Composite score | 0–100 overall confidence |
| Technical score | Technical alignment component |
| Volume score | Volume component |
| Sentiment score | News sentiment component |
| Trend | UPTREND / DOWNTREND / SIDEWAYS |
| RSI | Current RSI(14) |
| Active scanners | How many swing/intraday scanner signals fired today |

---

## Top 5 Picks

The five stocks with the highest composite score are highlighted at the top of the list as **Top 5 Picks**. These are the setups with the strongest multi-factor alignment — not recommendations, but the highest-conviction setups of the day based on the model's current weights.

---

## Filters

You can narrow the list by:
- Scanner type (filter to stocks that have a specific scanner fired)
- RSI range
- Volume ratio minimum
- Sector
- Minimum confidence score threshold

---

## Refreshing

The screener result is cached for **5 minutes** (300 seconds). If you need a fresher result, pull to refresh — a new score calculation is triggered.

---

## What Analysis Mode Is Not

- It is not a buy signal list
- It is not a guarantee of near-term price movement
- It does not account for news events happening right now (sentiment data has a ~4-hour lag from the RSS feed cycle)

Use the ranked list as an efficient starting point to identify which stocks have the most aligned signals today, then do your own analysis on the top candidates before acting.`,
};
